import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { IconType } from "react-icons";
import { LuArrowDownRight, LuArrowUpRight } from "react-icons/lu";
import { card } from "../../../app-components/ui/styles";

type Tone = "purple" | "green" | "amber" | "red" | "blue" | "slate";

const TONE: Record<Tone, string> = {
  purple: "bg-hc_F4EBFA text-honeConnectPurple",
  green: "bg-emerald-50 text-emerald-600",
  amber: "bg-amber-50 text-amber-600",
  red: "bg-red-50 text-red-600",
  blue: "bg-blue-50 text-blue-600",
  slate: "bg-slate-100 text-slate-600",
};

type StatCardProps = {
  label: string;
  value?: string;
  icon: IconType;
  tone?: Tone;
  /** % change vs previous period */
  delta?: number;
  /** small line under the value */
  hint?: ReactNode;
  to?: string;
  loading?: boolean;
};

const StatCard = ({ label, value, icon: Icon, tone = "purple", delta, hint, to, loading }: StatCardProps) => {
  const missing = !loading && (value === undefined || value === "–");

  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${TONE[tone]}`}>
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        {delta !== undefined && (
          <span
            className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-semibold ${
              delta >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
            }`}
            title="Compared with the previous period"
          >
            {delta >= 0 ? (
              <LuArrowUpRight className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <LuArrowDownRight className="h-3.5 w-3.5" aria-hidden />
            )}
            {Math.abs(delta).toFixed(1)}%
          </span>
        )}
      </div>
      <p className="mt-3 text-sm text-hcMuted sm:mt-4">{label}</p>
      {loading ? (
        <span className="mt-1.5 block h-8 w-28 animate-pulse rounded-md bg-hcBg" />
      ) : (
        <p
          className={`mt-1 text-xl font-semibold sm:text-[1.65rem] leading-tight tracking-tight ${
            missing ? "text-gray-300" : "text-honeConnectBlack"
          }`}
        >
          {missing ? "–" : value}
        </p>
      )}
      <p className="mt-1.5 min-h-[18px] text-xs text-hcMuted">
        {loading ? "" : missing ? "Not in API yet" : hint}
      </p>
    </>
  );

  const cls = `${card} block p-4 transition-shadow sm:p-5`;
  return to ? (
    <Link
      to={to}
      className={`${cls} hover:shadow-pop focus:outline-none focus-visible:ring-2 focus-visible:ring-honeConnectPurple/40`}
    >
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
};

export default StatCard;
